import { SensorStatus, SensorType } from "common";
import { Formik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, Input, Label, Select, YStack, Text, Spinner, ScrollView } from "tamagui";

import { useFieldValidationSchema } from "../../hooks/useFieldValidationSchema";

interface SensorFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sensor?: FormValues | null;
  hubId: string;
  onSubmit: (values: FormValues) => void;
}
export interface FormValues {
  hubId: string;
  name: string;
  location: string;
  dsk: string;
  type: string;
  version: string;
  serialNumber: string;
  status: string;
}

export function SensorFormModal({
  open,
  onOpenChange,
  sensor,
  hubId,
  onSubmit,
}: SensorFormModalProps) {
  const { t } = useTranslation();
  const [loading, setIsLoading] = useState(false);
  const { sensorFormValidationSchema } = useFieldValidationSchema();
  const mode = sensor ? "edit" : "create";

  const initialValues: FormValues = sensor
    ? {
        hubId,
        name: sensor.name,
        location: sensor.location,
        dsk: sensor.dsk,
        type: sensor.type,
        version: sensor.version,
        serialNumber: sensor.serialNumber,
        status: sensor.status,
      }
    : {
        hubId,
        name: "",
        location: "",
        dsk: "",
        type: "",
        version: "",
        serialNumber: "",
        status: "",
      };

  const handleSubmit = async (values: FormValues) => {
    setIsLoading(true);
    await onSubmit(values);
    onOpenChange(false);
    setIsLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Trigger asChild />
      <Dialog.Portal zIndex={1}>
        <Dialog.Overlay />
        <Dialog.Content bordered elevate size="$8" padding="$4" width="480px" maxHeight="90%">
          <ScrollView showsVerticalScrollIndicator style={{ maxHeight: "100%" }}>
            <Dialog.Title fontSize="$3" lineHeight="$3">
              {mode === "create" ? t("sensors.form.createTitle") : t("sensors.form.editTitle")}
            </Dialog.Title>
            <Formik
              initialValues={initialValues}
              validationSchema={sensorFormValidationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
              validateOnBlur={false}
              validateOnChange={false}
            >
              {({ handleChange, handleSubmit, values, errors }) => (
                <YStack space="$2" marginTop="$2">
                  <YStack>
                    <Label>{t("sensors.form.nameLabel")}</Label>
                    <Input
                      value={values.name}
                      onChangeText={handleChange("name")}
                      placeholder={t("sensors.form.namePlaceholder")}
                    />
                    {errors.name && (
                      <Text fontSize="$2" color="$red10" marginTop="$2">
                        {errors.name}
                      </Text>
                    )}
                  </YStack>
                  <YStack>
                    <Label>{t("sensors.form.locationLabel")}</Label>
                    <Input
                      value={values.location}
                      onChangeText={handleChange("location")}
                      placeholder={t("sensors.form.locationPlaceholder")}
                    />
                    {errors.location && (
                      <Text fontSize="$2" color="$red10" marginTop="$2">
                        {errors.location}
                      </Text>
                    )}
                  </YStack>
                  <YStack>
                    <Label>{t("sensors.form.dskLabel")}</Label>
                    <Input
                      value={values.dsk}
                      onChangeText={handleChange("dsk")}
                      placeholder={t("sensors.form.dskPlaceholder")}
                    />
                    {errors.dsk && (
                      <Text fontSize="$2" color="$red10" marginTop="$2">
                        {errors.dsk}
                      </Text>
                    )}
                  </YStack>
                  <YStack>
                    <Label>{t("sensors.form.typeLabel")}</Label>
                    <Select value={values.type} onValueChange={handleChange("type")}>
                      <Select.Trigger>
                        <Select.Value
                          placeholder={t("sensors.form.typePlaceholder")}
                          fontSize="$4"
                        />
                      </Select.Trigger>
                      <Select.Content>
                        <Select.ScrollUpButton />
                        <Select.Viewport>
                          <Select.Item value={SensorType.TEMPERATURE} index={0}>
                            <Select.ItemText>
                              {t("sensors.form.typeOptions.temperature")}
                            </Select.ItemText>
                          </Select.Item>
                          <Select.Item value={SensorType.HUMIDITY} index={1}>
                            <Select.ItemText>
                              {t("sensors.form.typeOptions.humidity")}
                            </Select.ItemText>
                          </Select.Item>
                          <Select.Item value={SensorType.PRESSURE} index={2}>
                            <Select.ItemText>
                              {t("sensors.form.typeOptions.pressure")}
                            </Select.ItemText>
                          </Select.Item>
                          <Select.Item value={SensorType.LIGHT} index={3}>
                            <Select.ItemText>{t("sensors.form.typeOptions.light")}</Select.ItemText>
                          </Select.Item>
                          <Select.Item value={SensorType.MOTION} index={4}>
                            <Select.ItemText>
                              {t("sensors.form.typeOptions.motion")}
                            </Select.ItemText>
                          </Select.Item>
                          <Select.Item value={SensorType.AIR_QUALITY} index={5}>
                            <Select.ItemText>
                              {t("sensors.form.typeOptions.airQuality")}
                            </Select.ItemText>
                          </Select.Item>
                          <Select.Item value={SensorType.WATER_QUALITY} index={6}>
                            <Select.ItemText>
                              {t("sensors.form.typeOptions.waterQuality")}
                            </Select.ItemText>
                          </Select.Item>
                          <Select.Item value={SensorType.HVAC_CONDENSER_MONITOR} index={7}>
                            <Select.ItemText>
                              {t("sensors.form.typeOptions.hvacCondenserMonitor")}
                            </Select.ItemText>
                          </Select.Item>
                          <Select.Item value={SensorType.HVAC_AIRHANDLER_MONITOR} index={8}>
                            <Select.ItemText>
                              {t("sensors.form.typeOptions.hvacAirhandlerMonitor")}
                            </Select.ItemText>
                          </Select.Item>
                          <Select.Item value={SensorType.ELECTRICAL} index={9}>
                            <Select.ItemText>
                              {t("sensors.form.typeOptions.electrical")}
                            </Select.ItemText>
                          </Select.Item>
                          <Select.Item value={SensorType.POWER} index={10}>
                            <Select.ItemText>{t("sensors.form.typeOptions.power")}</Select.ItemText>
                          </Select.Item>
                          <Select.Item value={SensorType.LEAK_DETECTOR} index={11}>
                            <Select.ItemText>
                              {t("sensors.form.typeOptions.leakDetector")}
                            </Select.ItemText>
                          </Select.Item>
                          <Select.Item value={SensorType.OTHER} index={12}>
                            <Select.ItemText>{t("sensors.form.typeOptions.other")}</Select.ItemText>
                          </Select.Item>
                        </Select.Viewport>
                      </Select.Content>
                    </Select>
                    {errors.type && (
                      <Text fontSize="$2" color="$red10" marginTop="$2">
                        {errors.type}
                      </Text>
                    )}
                  </YStack>
                  <YStack>
                    <Label>{t("sensors.form.versionLabel")}</Label>
                    <Input
                      value={values.version}
                      onChangeText={handleChange("version")}
                      placeholder={t("sensors.form.versionPlaceholder")}
                    />
                    {errors.version && (
                      <Text fontSize="$2" color="$red10" marginTop="$2">
                        {errors.version}
                      </Text>
                    )}
                  </YStack>
                  <YStack>
                    <Label>{t("sensors.form.serialLabel")}</Label>
                    <Input
                      value={values.serialNumber}
                      onChangeText={handleChange("serialNumber")}
                      placeholder={t("sensors.form.serialPlaceholder")}
                    />
                    {errors.serialNumber && (
                      <Text fontSize="$2" color="$red10" marginTop="$2">
                        {errors.serialNumber}
                      </Text>
                    )}
                  </YStack>
                  <YStack>
                    <Label>{t("sensors.form.statusLabel")}</Label>
                    <Select value={values.status} onValueChange={handleChange("status")}>
                      <Select.Trigger>
                        <Select.Value
                          placeholder={t("sensors.form.statusPlaceholder")}
                          fontSize="$4"
                        />
                      </Select.Trigger>
                      <Select.Content>
                        <Select.ScrollUpButton />
                        <Select.Viewport>
                          <Select.Item value={SensorStatus.Active} index={0}>
                            <Select.ItemText>
                              {t("sensors.form.statusOptions.active")}
                            </Select.ItemText>
                          </Select.Item>
                          <Select.Item value={SensorStatus.Inactive} index={1}>
                            <Select.ItemText>
                              {t("sensors.form.statusOptions.inactive")}
                            </Select.ItemText>
                          </Select.Item>
                          <Select.Item value={SensorStatus.Maintenance} index={1}>
                            <Select.ItemText>
                              {t("sensors.form.statusOptions.maintenance")}
                            </Select.ItemText>
                          </Select.Item>
                          <Select.Item value={SensorStatus.Pending} index={0}>
                            <Select.ItemText>
                              {t("sensors.form.statusOptions.pending")}
                            </Select.ItemText>
                          </Select.Item>
                        </Select.Viewport>
                      </Select.Content>
                    </Select>
                    {errors.status && (
                      <Text fontSize="$2" color="$red10" marginTop="$2">
                        {errors.status}
                      </Text>
                    )}
                  </YStack>
                  <YStack space="$2" marginTop="$4" flexDirection="row" justifyContent="flex-end">
                    <Button onPress={() => onOpenChange(false)} color={"$color12"}>
                      {t("common.cancel")}
                    </Button>
                    <Button
                      onPress={() => handleSubmit()}
                      backgroundColor={"$hgPrimary"}
                      disabled={loading}
                    >
                      {loading ? (
                        <Spinner color="white" size="small" />
                      ) : (
                        <Text>{t("common.submit")}</Text>
                      )}
                    </Button>
                  </YStack>
                </YStack>
              )}
            </Formik>
          </ScrollView>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}

export default SensorFormModal;
